import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/daglpphtto0ceqqnhv20";

const categories = ["宿舍趣闻", "课堂名场面", "食堂奇遇", "社团搞事", "期末求生", "恋爱吃瓜", "其他"];

interface Status {
  type: "idle" | "success" | "error";
  msg: string;
}

export default function Submit() {
  const [status, setStatus] = useState<Status>({ type: "idle", msg: "" });

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // anti-spam honeypot check
    const honeypot = String(fd.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "success", msg: "投稿已收到，感谢分享你的校园趣事，我们很快就会审核发布～" });
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const payload = new URLSearchParams();
    fd.forEach((value, key) => {
      if (typeof value === "string" && value.trim()) payload.append(key, value.trim());
    });

    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const code = parsed?.code;

      if (res.ok && code === "OK" && !/spam/i.test(responseText + serverMsg)) {
        setStatus({ type: "success", msg: "投稿成功！感谢分享你的校园趣事，审核通过后就会和大家见面～" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          msg: serverMsg || "投稿暂时没成功，请稍后再试，或者检查一下内容哦。",
        });
      }
    } catch {
      setStatus({ type: "error", msg: "网络好像开了个小差，请稍后再试～" });
    }
  };

  return (
    <section id="submit" className="scroll-mt-20 bg-background-100 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="text-xs md:text-sm font-medium tracking-wide text-accent-600">📮 独乐乐不如众乐乐</p>
            <h2 className="mt-2 font-heading text-2xl md:text-3xl text-foreground-950">
              分享你身边的校园趣事
            </h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-700">
              身边总有那么几件让人捧腹的瞬间，与其憋在心里，不如拿出来让更多人一起乐。
              无论是宿舍整活、课堂名场面还是食堂奇遇，欢迎你来投稿吃瓜。
            </p>

            <div className="mt-6 rounded-2xl bg-background-50 border border-background-200 p-5">
              <h3 className="font-heading text-base text-foreground-950">投稿前的小提醒</h3>
              <ul className="mt-3 flex flex-col gap-2 text-sm text-foreground-700">
                {[
                  "内容真实友善，不含人身攻击与敏感信息",
                  "记得隐去姓名、学院等隐私，只留下快乐",
                  "描述生动些，更有机会被更多人围观",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            data-readdy-form="xiaoyuan-guagua-submit"
            className="rounded-3xl bg-background-50 border border-background-200 p-6 md:p-8"
          >
            <div className="grid gap-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="nickname" className="text-sm text-foreground-800">
                    怎么称呼你 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="nickname"
                    name="nickname"
                    required
                    maxLength={20}
                    placeholder="例如：爱嗦粉的柚子"
                    className="rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="school" className="text-sm text-foreground-800">
                    学校或学院
                  </label>
                  <input
                    id="school"
                    name="school"
                    maxLength={30}
                    placeholder="可选，方便同学认亲"
                    className="rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="category" className="text-sm text-foreground-800">
                  选择分类 <span className="text-primary-500">*</span>
                </label>
                <select
                  id="category"
                  name="category"
                  required
                  className="rounded-xl border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400"
                >
                  <option value="">请选择</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label htmlFor="story" className="text-sm text-foreground-800">
                  说说你的趣事 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="story"
                  name="story"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="描述得生动一点，让更多人一起乐～（500字以内）"
                  className="resize-none rounded-xl border border-background-300 bg-background-50 px-4 py-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                ></textarea>
              </div>

              {/* honeypot guard */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="guard-field"
              />

              {status.type !== "idle" && (
                <p
                  role={status.type === "error" ? "alert" : "status"}
                  className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${
                    status.type === "success"
                      ? "bg-accent-100 text-accent-900"
                      : "bg-primary-100 text-primary-900"
                  }`}
                >
                  <i className={`mt-0.5 ${status.type === "success" ? "ri-checkbox-circle-line" : "ri-alert-line"}`}></i>
                  {status.msg}
                </p>
              )}

              <button
                type="submit"
                className="rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-send-plane-fill mr-1"></i>
                提交投稿
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}